import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import CreatorsLanding from "@/pages/CreatorsLanding";
import AgenciesLanding from "@/pages/AgenciesLanding";
import TrustPage from "@/pages/TrustPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AcceptableUsePage from "@/pages/AcceptableUsePage";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import CreatorDashboard from "@/pages/CreatorDashboard";
import AgencyDashboard from "@/pages/AgencyDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import VerifyLicense from "@/pages/VerifyLicense";
import CreatorPublicProfile from "@/pages/CreatorPublicProfile";
import InviteAccept from "@/pages/InviteAccept";
import CompleteProfile from "@/pages/CompleteProfile";
import AgencyProfileView from "@/pages/AgencyProfileView";

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  return (
    <Routes>
      <Route path="/" element={user ? (
        user.role === 'creator' ? <Navigate to="/creator" /> :
        user.role === 'admin' ? <Navigate to="/admin" /> :
        <Navigate to="/agency" />
      ) : <Landing />} />
      <Route path="/creators" element={<CreatorsLanding />} />
      <Route path="/agencies" element={<AgenciesLanding />} />
      <Route path="/trust" element={<TrustPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/acceptable-use" element={<AcceptableUsePage />} />
      <Route path="/login" element={user ? <Navigate to="/" /> : <Auth mode="login" />} />
      <Route path="/signup" element={user ? <Navigate to="/" /> : <Auth mode="signup" />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPassword />} />
      <Route path="/verify/:licenseId" element={<VerifyLicense />} />
      <Route path="/invite/accept" element={<InviteAccept />} />
      <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />
      <Route path="/creator/agencies/:id" element={<ProtectedRoute><AgencyProfileView /></ProtectedRoute>} />
      <Route path="/creator" element={<ProtectedRoute allowedRoles={['creator']}><CreatorDashboard /></ProtectedRoute>} />
      <Route path="/agency" element={<ProtectedRoute allowedRoles={['buyer', 'agency_admin']}><AgencyDashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/creator/:publicId" element={<CreatorPublicProfile />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster theme="light" position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
